import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Card, Button, Select, Table, Typography, Space,
  Row, Col, App as AntApp, Flex,
} from 'antd';
import {
  ReloadOutlined,
} from '@ant-design/icons';
import { API_URL } from '../../config';
import { getToken } from './utils';

const { Title, Text } = Typography;

// Custos AWS (Cost Explorer com cache 1h, bar chart SVG sem dependência externa).
export default function AdminAwsCustos() {
  const { message } = AntApp.useApp();
  const [dados, setDados] = useState<{
    moeda: string;
    meses: Array<{ mes: string; total: number; servicos: Record<string, number> }>;
    servicos_top: string[];
    cached?: boolean;
    cached_at?: string;
  } | null>(null);
  const [meses, setMeses] = useState(6);
  const [loading, setLoading] = useState(true);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/admin/aws-costs?meses=${meses}`,
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setDados(d);
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }
  useEffect(() => { carregar(); /* eslint-disable-line */ }, [meses]);

  if (loading || !dados) return <Card loading />;

  const fmtMoeda = (v: number) => '$ ' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtMes = (s: string) => {
    const [a, m] = s.split('-');
    const meses_pt = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
    return `${meses_pt[parseInt(m, 10) - 1]}/${a.slice(2)}`;
  };

  // Cores por serviço (estável por ordem do top)
  const CORES = ['#020D7F', '#0930D0', '#0a7f4a', '#B07500', '#B91626', '#7C3AED', '#0891B2', '#DB2777', '#888'];
  const corDe = (svc: string) => {
    const idx = dados.servicos_top.indexOf(svc);
    if (idx >= 0 && idx < CORES.length) return CORES[idx];
    if (svc === 'Outros') return '#888';
    return '#aaa';
  };

  // Total geral e variação mês a mês
  const totalGeral = dados.meses.reduce((s, m) => s + m.total, 0);
  const ultimo = dados.meses[dados.meses.length - 1];
  const penultimo = dados.meses[dados.meses.length - 2];
  const delta = ultimo && penultimo && penultimo.total > 0
    ? ((ultimo.total - penultimo.total) / penultimo.total) * 100 : null;

  // SVG bar chart sem dependência externa (recharts opcional)
  const maxTotal = Math.max(...dados.meses.map(m => m.total), 1);
  const altura = 280;
  const larguraBarra = 60;
  const gap = 24;
  const padding = 50;
  const larguraSVG = padding * 2 + dados.meses.length * (larguraBarra + gap);

  return (
    <div>
      <Flex justify="space-between" align="flex-start" wrap="wrap" gap={16} style={{ marginBottom: 16 }}>
        <div>
          <Title level={4} style={{ margin: 0, color: '#020D7F' }}>☁️ Custos AWS</Title>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Dados via AWS Cost Explorer. Cache 1h ({dados.cached ? `de ${new Date(dados.cached_at!).toLocaleString('pt-BR')}` : 'fresh agora'}).
          </Text>
        </div>
        <Space>
          <Select value={meses} onChange={setMeses} size="small" style={{ width: 100 }}
            options={[3, 6, 9, 12].map(n => ({ value: n, label: `${n} meses` }))} />
          <Button icon={<ReloadOutlined />} onClick={carregar} size="small">Atualizar</Button>
        </Space>
      </Flex>

      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small" styles={{ body: { borderLeft: '4px solid #020D7F' } }}>
            <Text type="secondary" style={{ fontSize: 10 }}>Total ({meses} meses)</Text>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#020D7F' }}>{fmtMoeda(totalGeral)}</div>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" styles={{ body: { borderLeft: '4px solid #0930D0' } }}>
            <Text type="secondary" style={{ fontSize: 10 }}>Último mês fechado</Text>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#0930D0' }}>{ultimo ? fmtMoeda(ultimo.total) : '—'}</div>
            <Text type="secondary" style={{ fontSize: 10 }}>{ultimo ? fmtMes(ultimo.mes) : ''}</Text>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" styles={{ body: { borderLeft: `4px solid ${delta != null && delta > 0 ? '#B91626' : '#0a7f4a'}` } }}>
            <Text type="secondary" style={{ fontSize: 10 }}>Variação vs mês anterior</Text>
            <div style={{ fontSize: 18, fontWeight: 700, color: delta != null && delta > 0 ? '#B91626' : '#0a7f4a' }}>
              {delta != null ? `${delta > 0 ? '+' : ''}${delta.toFixed(1)}%` : '—'}
            </div>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" styles={{ body: { borderLeft: '4px solid #B07500' } }}>
            <Text type="secondary" style={{ fontSize: 10 }}>Média mensal</Text>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#B07500' }}>{fmtMoeda(totalGeral / dados.meses.length)}</div>
          </Card>
        </Col>
      </Row>

      <Card size="small" title="Gasto por mês (USD)" style={{ marginBottom: 16, overflowX: 'auto' }}>
        <svg width={larguraSVG} height={altura + 60} style={{ display: 'block' }}>
          {/* Eixo Y - linhas de grid */}
          {[0, 0.25, 0.5, 0.75, 1].map((f, i) => {
            const y = altura - f * (altura - 20) + 10;
            return (
              <g key={i}>
                <line x1={padding} x2={larguraSVG - padding / 2} y1={y} y2={y} stroke="#eee" strokeDasharray="3 3" />
                <text x={padding - 6} y={y + 3} fontSize="9" fill="#888" textAnchor="end">{fmtMoeda(f * maxTotal)}</text>
              </g>
            );
          })}
          {/* Barras empilhadas */}
          {dados.meses.map((m, i) => {
            const x = padding + i * (larguraBarra + gap);
            let yAcum = altura + 10;
            const servicos = Object.entries(m.servicos).sort((a, b) => b[1] - a[1]);
            return (
              <g key={m.mes}>
                {servicos.map(([svc, val]) => {
                  const h = (val / maxTotal) * (altura - 20);
                  yAcum -= h;
                  return (
                    <rect key={svc} x={x} y={yAcum} width={larguraBarra} height={h}
                      fill={corDe(svc)} stroke="#fff" strokeWidth={0.5}>
                      <title>{svc}: {fmtMoeda(val)}</title>
                    </rect>
                  );
                })}
                <text x={x + larguraBarra / 2} y={altura + 28} fontSize="10" fill="#444" textAnchor="middle">{fmtMes(m.mes)}</text>
                <text x={x + larguraBarra / 2} y={altura + 42} fontSize="9" fill="#888" textAnchor="middle">{fmtMoeda(m.total)}</text>
              </g>
            );
          })}
        </svg>

        {/* Legenda */}
        <Flex wrap="wrap" gap={8} style={{ marginTop: 12, fontSize: 11 }}>
          {[...dados.servicos_top, 'Outros'].map(svc => (
            <Flex key={svc} align="center" gap={4}>
              <div style={{ width: 12, height: 12, background: corDe(svc), borderRadius: 2 }} />
              <Text style={{ fontSize: 11 }}>{svc}</Text>
            </Flex>
          ))}
        </Flex>
      </Card>

      <Card size="small" title="Top serviços do último mês">
        <Table
          rowKey="svc"
          size="small"
          pagination={false}
          dataSource={ultimo ? Object.entries(ultimo.servicos).sort((a, b) => b[1] - a[1]).map(([svc, val]) => ({ svc, val })) : []}
          columns={[
            { title: 'Serviço', dataIndex: 'svc', key: 's',
              render: (s: string) => (
                <Space>
                  <div style={{ width: 10, height: 10, background: corDe(s), borderRadius: 2 }} />
                  <Text>{s}</Text>
                </Space>
              ) },
            { title: 'Custo', dataIndex: 'val', key: 'v', align: 'right',
              render: (v: number) => <Text strong>{fmtMoeda(v)}</Text> },
            { title: '%', key: 'pct', align: 'right', width: 80,
              render: (_: unknown, r: { val: number }) => (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {((r.val / (ultimo?.total || 1)) * 100).toFixed(1)}%
                </Text>
              ) },
          ]}
        />
      </Card>
    </div>
  );
}
